import { STATI_CHIUSI, type RichiestaFields, type Stato, type Tipo } from "@app/schema";
import { where, type FieldsPatch, type ListItem } from "@app/sp-data";
import type { DataServices } from "./DataProvider";
import type { ArticoloRef, StampanteRef } from "./catalogo";
import { storage } from "../lib/storage";

/**
 * Logica applicativa delle richieste: le pagine chiamano queste funzioni,
 * non i repository direttamente.
 */

export type Richiesta = ListItem<RichiestaFields>;
const q = where<RichiestaFields>();

const round2 = (n: number) => Math.round(n * 100) / 100;

/* ------------------------------ Richiedente ------------------------------ */

/**
 * Richieste dell'utente corrente.
 * Per i coordinatori SharePoint restituisce già solo le proprie (permessi a livello
 * di elemento); il filtro lato client serve per chi è anche gestore e vede tutto.
 */
export async function loadMine(s: DataServices, nextLink?: string) {
  const page = nextLink
    ? await s.richieste.next(nextLink, true)
    : await s.richieste.query({ orderBy: { field: "Created", desc: true }, top: 50, allowNonIndexed: true });
  const mine = (r: Richiesta) =>
    r.createdBy?.id === s.me.id ||
    [s.me.mail, s.me.userPrincipalName].some((m) => m && r.createdBy?.email?.toLowerCase() === m.toLowerCase());
  return { items: s.roles.gestore ? page.items.filter(mine) : page.items, nextLink: page.nextLink };
}

interface NuovaBase {
  servizio: string;
  quantita: number;
  urgente: boolean;
  note: string;
}

/** Richiesta di materiale per stampanti: articolo scelto dal catalogo. */
export interface NuovaStampanti extends NuovaBase {
  tipo: "Stampanti";
  stampante: StampanteRef;
  articolo: ArticoloRef;
}

/** Cancelleria, Mensa, Generica: categoria e descrizione libera. */
export interface NuovaGenerica extends NuovaBase {
  tipo: Exclude<Tipo, "Stampanti">;
  categoria: string;
  descrizione: string;
}

export type NuovaRichiesta = NuovaStampanti | NuovaGenerica;

/** Valore proposto per una richiesta da catalogo (IVA esclusa). */
export const valoreProposto = (quantita: number, prezzo?: number) =>
  prezzo == null || !Number.isFinite(quantita) ? undefined : round2(quantita * prezzo);

/** Riassunto mostrato negli elenchi (colonna Title). */
function titolo(r: NuovaRichiesta): string {
  if (r.tipo === "Stampanti") return r.articolo.nome.slice(0, 255);
  const riga = r.descrizione.trim().split(/\r?\n/)[0] ?? "";
  return (riga.length > 80 ? `${riga.slice(0, 79)}…` : riga) || r.categoria;
}

function campi(r: NuovaRichiesta): RichiestaFields {
  const comuni: RichiestaFields = {
    Title: titolo(r),
    Tipo: r.tipo,
    Servizio: r.servizio,
    Quantita: r.quantita,
    Priorita: r.urgente ? "Urgente" : "Standard",
    Note: r.note.trim() || undefined,
    Stato: "Sottomessa",
  };
  if (r.tipo === "Stampanti") {
    return {
      ...comuni,
      Stampante: r.stampante.id,
      Articolo: r.articolo.id,
      Descrizione: r.articolo.descrizione?.trim() || r.articolo.nome,
      Codice: r.articolo.codice || undefined,
      PrezzoUnitario: r.articolo.prezzoNetto,
      Valore: valoreProposto(r.quantita, r.articolo.prezzoNetto),
    };
  }
  return { ...comuni, Categoria: r.categoria, Descrizione: r.descrizione.trim() };
}

/** Crea la richiesta e imposta il richiedente. */
export async function inviaRichiesta(s: DataServices, r: NuovaRichiesta): Promise<number> {
  const cachedLookup = myLookupId.get(s);
  const item = await s.richieste.create({ ...campi(r), Richiedente: cachedLookup });

  // Prima richiesta dell'utente: il LookupId si ricava dall'autore appena registrato
  if (!cachedLookup && item.authorLookupId) {
    myLookupId.set(s, item.authorLookupId);
    try {
      await s.richieste.update(item.id, { Richiedente: item.authorLookupId });
    } catch {
      /* non bloccante: "Creato da" contiene comunque l'autore */
    }
  }

  ricordaScelte(s, r);
  return item.id;
}

/* ------------------------------- Gestore --------------------------------- */

export const FILTRI_STATO = {
  "Da gestire": ["Sottomessa", "Presa visione"],
  "In corso": ["In lavorazione", "Bloccata"],
  Chiuse: ["Evasa", "Respinta"],
  Tutte: [],
} as const satisfies Record<string, readonly Stato[]>;
export type FiltroStato = keyof typeof FILTRI_STATO;

export interface FiltriCoda {
  stato: FiltroStato;
  /** Tipi da mostrare; vuoto = tutti. */
  tipi: Tipo[];
  /** Servizio esatto; undefined = tutti. */
  servizio?: string;
}

/** Filtro OData sulle colonne indicizzate Stato, Tipo e Servizio. */
export function filtroCoda(f: FiltriCoda): string | undefined {
  const stati = FILTRI_STATO[f.stato] as readonly Stato[];
  const parti = [
    stati.length ? q.or(...stati.map((st) => q.eq("Stato", st))) : undefined,
    f.tipi.length ? q.or(...f.tipi.map((t) => q.eq("Tipo", t))) : undefined,
    f.servizio ? q.eq("Servizio", f.servizio) : undefined,
  ].filter((x): x is string => !!x);
  if (!parti.length) return undefined;
  return parti.length === 1 ? parti[0] : q.and(...parti);
}

export async function loadCoda(s: DataServices, f: FiltriCoda, nextLink?: string) {
  return nextLink
    ? s.richieste.next(nextLink, true)
    : s.richieste.query({
        filter: filtroCoda(f),
        orderBy: { field: "Created", desc: true },
        top: 50,
        allowNonIndexed: true,
      });
}

/** Urgenti in cima, poi dalla più recente. */
export function ordinaCoda(items: Richiesta[]): Richiesta[] {
  return [...items].sort((a, b) => {
    const ua = a.fields.Priorita === "Urgente" ? 0 : 1;
    const ub = b.fields.Priorita === "Urgente" ? 0 : 1;
    return ua - ub || b.id - a.id;
  });
}

/**
 * Tipi assegnati all'utente corrente nell'elenco Gestori acquisti:
 * servono come filtro iniziale della coda. Nessuna riga = tutti i tipi.
 */
export async function loadMieiTipi(s: DataServices): Promise<Tipo[]> {
  try {
    const me = await resolveMyLookupId(s);
    if (!me) return [];
    const righe = await s.gestori.queryAll({ top: 500 });
    return [...new Set(righe.filter((r) => r.fields.Gestore === me && r.fields.Tipo).map((r) => r.fields.Tipo!))];
  } catch {
    return [];
  }
}

export interface Gestione {
  stato: Stato;
  quantita?: number;
  valore?: number;
  extraBudget: boolean;
  noteGestore: string;
}

/**
 * Salva le modifiche del gestore. Imposta automaticamente DataChiusura
 * (stati finali) e GestitoDa. Con l'ETag, se qualcun altro ha modificato
 * la richiesta nel frattempo, Graph risponde 412 (GraphError.isConflict).
 */
export async function salvaGestione(s: DataServices, r: Richiesta, g: Gestione): Promise<void> {
  const patch: FieldsPatch<RichiestaFields> = {
    Stato: g.stato,
    Quantita: g.quantita ?? null,
    Valore: g.valore ?? null,
    ExtraBudget: g.extraBudget,
    NoteGestore: g.noteGestore.trim() || null,
  };

  const eraChiusa = STATI_CHIUSI.includes(r.fields.Stato as Stato);
  const saraChiusa = STATI_CHIUSI.includes(g.stato);
  if (saraChiusa && !eraChiusa) patch.DataChiusura = new Date().toISOString();
  if (!saraChiusa && eraChiusa) patch.DataChiusura = null;

  const statoCambiato = g.stato !== r.fields.Stato;
  let lookup = statoCambiato ? await resolveMyLookupId(s) : undefined;
  if (lookup) patch.GestitoDa = lookup;

  const res = await s.richieste.update(r.id, patch, r.etag);

  // LookupId non risolvibile prima: si ricava dall'editor appena registrato
  if (statoCambiato && !lookup && res.EditorLookupId != null) {
    lookup = Number(res.EditorLookupId);
    myLookupId.set(s, lookup);
    await s.richieste.update(r.id, { GestitoDa: lookup });
  }
}

async function resolveMyLookupId(s: DataServices): Promise<number | undefined> {
  const cached = myLookupId.get(s);
  if (cached) return cached;
  for (const email of [s.me.mail, s.me.userPrincipalName]) {
    if (!email) continue;
    try {
      const u = await s.users.byEmail(email);
      if (u) {
        myLookupId.set(s, u.lookupId);
        return u.lookupId;
      }
    } catch {
      /* lista utenti non leggibile: si ripiega sull'editor */
    }
  }
  return undefined;
}

/* --------------------------- Preferenze locali --------------------------- */

const myLookupId = {
  key: (s: DataServices) => `lookup:${s.site.id}:${s.me.id}`,
  get: (s: DataServices) => {
    const v = storage.get(myLookupId.key(s));
    return v ? Number(v) : undefined;
  },
  set: (s: DataServices, id: number) => storage.set(myLookupId.key(s), String(id)),
};

/** Ultime scelte del richiedente, per precompilare il modulo successivo. */
export interface SceltePrecedenti {
  servizio?: string;
  stampanteId?: number;
}

export function sceltePrecedenti(s: DataServices): SceltePrecedenti {
  try {
    return JSON.parse(storage.get(`scelte:${s.me.id}`) ?? "") as SceltePrecedenti;
  } catch {
    return {};
  }
}

function ricordaScelte(s: DataServices, r: NuovaRichiesta) {
  const prev = sceltePrecedenti(s);
  storage.set(
    `scelte:${s.me.id}`,
    JSON.stringify({ servizio: r.servizio, stampanteId: r.tipo === "Stampanti" ? r.stampante.id : prev.stampanteId }),
  );
}
